import { Component, OnInit, QueryList, ViewChild, ViewChildren, inject } from "@angular/core";
import { BootstrapService } from "../shared/bootstrap/bootstrap.service";
import { FormBuilder, FormControl, FormGroup, NgForm, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { forkJoin, Observable } from "rxjs";
import { debounceTime, finalize, map, switchMap, tap } from "rxjs/operators";
import { FlightAttribute, FlightsService } from "../flights-calendar/flights.service";
import { LocalStorageService } from "../shared/local-storage.service";
import { Airport } from "../shared/models/airport.model";
import moment from "moment";
import { MatCheckbox, MatCheckboxChange } from "@angular/material/checkbox";
import { Flight } from "../shared/models/flight.model";
import { ActivatedRoute, Router } from "@angular/router";
import { PassengersService } from "../passengers/passengers.service";
import { PilotRole } from "../shared/models/pilotRole.model";
import { SharedService } from "../shared/shared.service";
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";

interface ChipItem {
    id: number;
    name: string;
}

@Component({
    selector: "app-update-flight",
    templateUrl: "./update-flight.component.html",
    styleUrls: ["./update-flight.component.scss"],
    standalone: false,
})
export class UpdateFlightComponent implements OnInit {
    private readonly _bootstrapService = inject(BootstrapService);

    @ViewChild("flightForm") flightForm: NgForm;
    @ViewChildren("fligtOperationCheckbox") private fligtOperationCheckbox: QueryList<MatCheckbox>;

    public appearanceType = "legacy";

    public parentFlight = true;
    public parentFlightCode = "";
    public minArrivalDate = new Date();
    public flightStatus = 1;

    public flightFormGroup: FormGroup;
    public passengerFormGroup: FormGroup;
    public flightCodeFormControl = new FormControl("", Validators.required);

    public pilotOptions: string[] = [];
    public filteredPilotOptions: Observable<string[]>;

    public filteredDepartureAirportOptions: Airport[];
    public filteredArrivalAirportOptions: Airport[];
    public filteredFlightCodeOptions: { id: number; code: string }[];

    public helicopterArray: ChipItem[] = [];
    public pilotArray: ChipItem[] = [];
    public flightAttr: { flightOperations: FlightAttribute[]; flightTypes: FlightAttribute[] };
    public pilotRoles: PilotRole[] = [];

    public loading = true;
    public departureIsLoading = false;
    public arrivalIsLoading = false;
    public flightCodeIsLoading = false;

    public passengerAmountDisabled = true;

    private _userSessionObject;

    constructor(
        private _formBuilder: FormBuilder,
        private _snackBar: MatSnackBar,
        private _translate: TranslateService,
        private _flightsService: FlightsService,
        private _localStorageService: LocalStorageService,
        private _sharedService: SharedService,
        private _acivatedRoute: ActivatedRoute,
        private _router: Router,
        private _passengersService: PassengersService,
    ) {
        this._userSessionObject = this._localStorageService.getUserLocalStorageObject();
        this.flightFormGroup = this._formBuilder.group({
            id: [null],
            departureId: [""],
            arrivalId: [""],
            departurePlace: ["", Validators.required],
            arrivalPlace: ["", Validators.required],
            departureTime: ["", [Validators.required, Validators.pattern(/((?:[01]\d|2[0-3]):[0-5]\d$)/g)]],
            arrivalTime: ["", [Validators.required, Validators.pattern(/((?:[01]\d|2[0-3]):[0-5]\d$)/g)]],
            flightDate: ["", Validators.required],
            arrivalDate: ["", Validators.required],
            flightTypeId: ["", Validators.required],
            duration: [{ value: "", disabled: true }, Validators.required],
            pilotId: [""],
            pilotRoleId: [""],
            coPilotId: [""],
            coPilotName: [""],
            coPilotRoleId: [""],
            helicopterId: [""],
            passengers: [{ value: 0, disabled: true }, [Validators.min(0), Validators.required]],
            notes: [""],
            clientName: ["", Validators.required],
            pilotNotes: [""],
            parentId: [0],
            sourceType: [1],
            status: [1, Validators.required],
        });
        this.pilotRoles = this._bootstrapService.pilotRoles();
    }

    ngOnInit(): void {
        const params: any = this._acivatedRoute.snapshot.params;
        forkJoin([
            this._flightsService.getFlightAttributes(),
            this._flightsService.getPilotesByOrgId(this._userSessionObject.organizationId),
            this._flightsService.getHelicoptersByOrgId(this._userSessionObject.organizationId),
            this._flightsService.getFlightById(params.flightId),
        ]).subscribe(
            (result) => {
                this.flightAttr = result[0];
                this.pilotArray = result[1].map((p) => <ChipItem>{ id: p.id, name: p.firstName + " " + p.lastName });
                this.pilotArray.map((pilot) => this.pilotOptions.push(pilot.name));
                this.helicopterArray = result[2].map((h) => <ChipItem>{ id: h.id, name: h.registrationMark });

                this.filteredPilotOptions = this.flightFormGroup.get("coPilotName").valueChanges.pipe(map((value) => this._filterPilot(value)));

                /** FILTER FLIGHT CODE */
                this.flightCodeFormControl.valueChanges
                    .pipe(
                        debounceTime(300),
                        tap(() => (this.flightCodeIsLoading = true)),
                        switchMap((value) => this._flightsService.searchFlightsByCode({ name: value }).pipe(finalize(() => (this.flightCodeIsLoading = false)))),
                    )
                    .subscribe((res) => (this.filteredFlightCodeOptions = res));
                /** END */

                this.flightFormGroup.valueChanges.subscribe((value) => {
                    if (value.departureTime && value.arrivalTime) {
                        const flightDate = moment(this.flightFormGroup.get("flightDate").value).format("YYYY-MM-DD");
                        const arrivalDate = moment(this.flightFormGroup.get("arrivalDate").value).format("YYYY-MM-DD");
                        const departureTime = moment(flightDate + " " + value.departureTime, "YYYY-MM-DD HH:mm:ss");
                        const arrivalTime = moment(arrivalDate + " " + value.arrivalTime, "YYYY-MM-DD HH:mm:ss");
                        if (arrivalTime.isBefore(departureTime)) {
                            this.flightFormGroup.get("arrivalTime").setErrors({ isBeforeError: true });
                            this.flightFormGroup.get("arrivalTime").markAsTouched();
                            return;
                        }
                        const ms = moment(arrivalTime, "YYYY-MM-DD HH:mm:ss").diff(moment(departureTime, "YYYY-MM-DD HH:mm:ss"));
                        const d = moment.duration(ms);
                        const s = Math.floor(d.asHours()) + moment.utc(ms).format(":mm");
                        this.flightFormGroup.patchValue({ duration: s }, { emitEvent: false });
                    }
                });

                this.flightFormGroup.get("flightDate").valueChanges.subscribe((date) => {
                    this.minArrivalDate = date;
                    this.flightFormGroup.patchValue({ arrivalDate: date });
                });

                const flightObject: any = result[3];
                this.flightStatus = flightObject.status;
                this.flightFormGroup.patchValue(flightObject);
                console.log(this.flightFormGroup.getRawValue());
                this.loading = false;
            },
            (error) => {
                this._router.navigate(["/flights"]);
            },
        );
    }

    onFlightCodeOptionSelected(event: MatAutocompleteSelectedEvent) {
        const object = event.option.value as { id: number; code: string };
        if (object) {
            this.flightCodeFormControl.setValue(object.code);
            this.flightFormGroup.patchValue({ parentId: object.id }, { emitEvent: false });
        }
    }

    onFormSubmit() {
        this.flightFormGroup.get("coPilotRoleId").setErrors(null);
        this.flightFormGroup.get("pilotRoleId").setErrors(null);
        this.flightCodeFormControl.setErrors(null);
        if (this.flightFormGroup.get("coPilotName").value !== "") {
            if (this.flightFormGroup.get("coPilotRoleId").value === "") {
                this.flightFormGroup.get("coPilotRoleId").setErrors({ required: true });
                this.flightFormGroup.get("coPilotRoleId").markAsTouched();
            }
        }
        if (this.flightFormGroup.get("pilotId").value !== "") {
            if (this.flightFormGroup.get("pilotRoleId").value === "") {
                this.flightFormGroup.get("pilotRoleId").setErrors({ required: true });
                this.flightFormGroup.get("pilotRoleId").markAsTouched();
            }
        }
        const flightDate = moment(this.flightFormGroup.get("flightDate").value);
        const arrivalDate = moment(this.flightFormGroup.get("arrivalDate").value);
        if (arrivalDate.isBefore(flightDate)) {
            this.flightFormGroup.get("arrivalDate").setErrors({ beforeError: true });
            this.flightFormGroup.get("arrivalDate").markAsTouched();
        }

        if (!this.parentFlight && this.flightCodeFormControl.value === "") {
            this.flightCodeFormControl.setErrors({ required: true });
            this.flightCodeFormControl.markAsTouched();
        }

        if (this.flightForm.valid) {
            const coPilotId = this._getCoPilotId();
            const flightObject = { ...this.flightFormGroup.getRawValue() } as Flight;
            flightObject.flightDate = moment(flightObject.flightDate).format("YYYY-MM-DD");
            flightObject.arrivalDate = moment(flightObject.arrivalDate).format("YYYY-MM-DD");
            flightObject.coPilotId = coPilotId ? coPilotId.id : null;
            flightObject.status = this.flightStatus;
            this._flightsService.updateFlight(flightObject).subscribe((result) => {
                this._snackBar.open(this._translate.instant("GENERAL.savedSuccessMsg"), this._translate.instant("GENERAL.close"), {
                    duration: 3000,
                });
            });
        } else {
            this._sharedService.checkIfFormGroupHasError(this.flightFormGroup).map((error) => {
                this.flightFormGroup.get(error.key).setErrors({ required: true });
                this.flightFormGroup.get(error.key).markAsTouched();
            });
        }
    }

    private _getCoPilotId() {
        if (!this.flightFormGroup.get("coPilotName").value) {
            return null;
        }
        const coPilotName = this.flightFormGroup.get("coPilotName").value.toLocaleLowerCase();
        return this.pilotArray.filter((pilot) => {
            const objCoPilotName = pilot.name.toLocaleLowerCase();
            return objCoPilotName === coPilotName;
        })[0];
    }

    private _filterPilot(value: string): string[] {
        const filterValue = value.toLowerCase();
        return this.pilotOptions.filter((option) => option.toLowerCase().indexOf(filterValue) === 0);
    }
}
